import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';

// Brand particle morph (vanilla Three.js): a dense point cloud assembles into the VH mark
// (dark V + orange H, sampled from the real logo), then — through a scatter-and-reform
// transition — morphs into the Bolt lightning logo, ping-ponging on a loop. Keeps the
// manner: gentle shimmer + an interactive cursor-repulsion wake. `light`/`bgColor` set
// palette + canvas blend. Reduced-motion safe (static VH).

function makeDot() {
  const S = 64;
  const c = document.createElement('canvas'); c.width = S; c.height = S;
  const x = c.getContext('2d');
  const g = x.createRadialGradient(S / 2, S / 2, 0, S / 2, S / 2, S / 2);
  g.addColorStop(0, 'rgba(255,255,255,1)'); g.addColorStop(0.35, 'rgba(255,255,255,0.5)'); g.addColorStop(1, 'rgba(255,255,255,0)');
  x.fillStyle = g; x.fillRect(0, 0, S, S);
  return new THREE.CanvasTexture(c);
}

function loadImg(src) {
  return new Promise((res, rej) => { const im = new Image(); im.onload = () => res(im); im.onerror = rej; im.src = src; });
}

// sample N points from a logo image's opaque pixels → {pos, col}; preserves dark vs orange
function sampleImage(img, N, S, rand, darkCol, orangeCol) {
  const maxDim = 260; const sc = maxDim / Math.max(img.width, img.height);
  const w = Math.max(1, Math.round(img.width * sc)), h = Math.max(1, Math.round(img.height * sc));
  const c = document.createElement('canvas'); c.width = w; c.height = h;
  const x = c.getContext('2d'); x.drawImage(img, 0, 0, w, h);
  const data = x.getImageData(0, 0, w, h).data;
  const cand = []; let minX = w, maxX = 0, minY = h, maxY = 0;
  for (let py = 0; py < h; py++) for (let px = 0; px < w; px++) {
    const i4 = (py * w + px) * 4;
    if (data[i4 + 3] > 130) {
      cand.push(px, py, data[i4], data[i4 + 1], data[i4 + 2]);
      if (px < minX) minX = px; if (px > maxX) maxX = px; if (py < minY) minY = py; if (py > maxY) maxY = py;
    }
  }
  const M = cand.length / 5 || 1;
  const bw = (maxX - minX) || 1, bh = (maxY - minY) || 1;
  const cxC = (minX + maxX) / 2, cyC = (minY + maxY) / 2;
  const scale = S / Math.max(bw, bh);
  const pos = new Float32Array(N * 3), col = new Float32Array(N * 3);
  const tmp = new THREE.Color();
  for (let i = 0; i < N; i++) {
    const k = ((rand() * M) | 0) * 5;
    const px = cand[k], py = cand[k + 1], r = cand[k + 2], g = cand[k + 3], b = cand[k + 4];
    pos[i * 3] = (px - cxC) * scale + (rand() - 0.5) * 0.02;
    pos[i * 3 + 1] = -(py - cyC) * scale + (rand() - 0.5) * 0.02;
    pos[i * 3 + 2] = (rand() - 0.5) * 0.22;
    const orange = r > 150 && g > 70 && g < 205 && b < 135;
    tmp.copy(orange ? orangeCol : darkCol);
    col[i * 3] = tmp.r; col[i * 3 + 1] = tmp.g; col[i * 3 + 2] = tmp.b;
  }
  return { pos, col };
}

const smooth = (v) => v * v * (3 - 2 * v);

export default function LogoMorphScene({ light = false, bgColor = null, transparent = false }) {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return undefined;
    try {
      const test = document.createElement('canvas');
      if (!(test.getContext('webgl') || test.getContext('experimental-webgl'))) return undefined;
    } catch { return undefined; }

    const reduce = typeof window !== 'undefined' && window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const bg = bgColor != null ? bgColor : (light ? 0xf6f5f2 : 0x16171b);
    const darkCol = new THREE.Color(light ? '#2B2F36' : '#D7E2F2');
    const orangeCol = new THREE.Color('#FF6A00');

    let width = mount.clientWidth || 700;
    let height = mount.clientHeight || 460;

    const scene = new THREE.Scene();
    if (!transparent) scene.fog = new THREE.FogExp2(bg, 0.055);
    const camera = new THREE.PerspectiveCamera(46, width / height, 0.1, 100);
    camera.position.set(0, 0, 6.2);
    camera.lookAt(0, 0, 0);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: transparent });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(width, height);
    if (transparent) renderer.setClearColor(0x000000, 0); else renderer.setClearColor(bg, 1);
    if ('outputColorSpace' in renderer) renderer.outputColorSpace = THREE.SRGBColorSpace;
    mount.appendChild(renderer.domElement);

    const group = new THREE.Group();
    scene.add(group);

    let disposed = false, raf = 0, geo = null, mat = null, dotTex = null;

    let targetRY = 0, targetRX = 0, mx = 0, my = 0, hovering = false;
    const onPointer = (e) => {
      const r = mount.getBoundingClientRect();
      const px = (e.clientX - r.left) / r.width, py = (e.clientY - r.top) / r.height;
      hovering = px >= -0.05 && px <= 1.05 && py >= -0.05 && py <= 1.05;
      mx = px - 0.5; my = py - 0.5; targetRY = mx * 0.28; targetRX = my * 0.18;
    };
    const onLeave = () => { hovering = false; };
    if (!reduce) { window.addEventListener('pointermove', onPointer, { passive: true }); mount.addEventListener('pointerleave', onLeave, { passive: true }); }
    const onResize = () => { width = mount.clientWidth || width; height = mount.clientHeight || height; camera.aspect = width / height; camera.updateProjectionMatrix(); renderer.setSize(width, height); };
    const ro = new ResizeObserver(onResize); ro.observe(mount);

    Promise.all([loadImg('/vh-mark-ink.png'), loadImg('/bolt-mark-ink.png')]).then(([imgVH, imgBolt]) => {
      if (disposed) return;
      const isMobile = width < 560;
      const N = isMobile ? 8000 : 15000;   // high pixel count for a dense, premium read
      const S = 3.0;
      let seed = 24680;
      const rand = () => { seed = (seed * 1664525 + 1013904223) >>> 0; return seed / 4294967296; };

      const A = sampleImage(imgVH, N, S, rand, darkCol, orangeCol);          // VH
      const B = sampleImage(imgBolt, N, S * 1.05, rand, darkCol, orangeCol); // Bolt
      const pos = new Float32Array(A.pos);
      const col = new Float32Array(A.col);
      const disp = new Float32Array(N * 3);
      const delay = new Float32Array(N), sdx = new Float32Array(N), sdy = new Float32Array(N), sdz = new Float32Array(N), ph = new Float32Array(N);
      for (let i = 0; i < N; i++) {
        delay[i] = rand();
        const a = rand() * Math.PI * 2, e = rand() * Math.PI - Math.PI / 2;
        sdx[i] = Math.cos(a) * Math.cos(e); sdy[i] = Math.sin(e); sdz[i] = Math.sin(a) * Math.cos(e);
        ph[i] = rand() * Math.PI * 2;
      }

      geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
      geo.setAttribute('color', new THREE.BufferAttribute(col, 3));
      dotTex = makeDot();
      mat = new THREE.PointsMaterial({
        size: isMobile ? 0.044 : 0.036, map: dotTex, vertexColors: true, transparent: true,
        opacity: 0.95, depthWrite: false, blending: light ? THREE.NormalBlending : THREE.AdditiveBlending, sizeAttenuation: true,
      });
      group.add(new THREE.Points(geo, mat));

      const cursor = new THREE.Vector3();
      const RAD = 1.5, STR = 0.13, EASE = 0.85;
      const CYCLE = 10, DWELL = 3, RAMP = 2, SPREAD = 0.4;
      const morphAmount = (t) => {
        const p = t % CYCLE;
        if (p < DWELL) return 0;
        if (p < DWELL + RAMP) return (p - DWELL) / RAMP;
        if (p < DWELL + RAMP + DWELL) return 1;
        return 1 - (p - (2 * DWELL + RAMP)) / RAMP;
      };
      const cVH = new THREE.Color(), cB = new THREE.Color(), cM = new THREE.Color();

      if (reduce) { renderer.render(scene, camera); return; }

      const clock = new THREE.Clock();
      const render = () => {
        const t = clock.getElapsedTime();
        const swayY = Math.sin(t * 0.18) * 0.07;
        group.rotation.y += ((targetRY + swayY) - group.rotation.y) * 0.05;
        group.rotation.x += (targetRX - group.rotation.x) * 0.05;
        camera.position.x += (targetRY * 0.7 - camera.position.x) * 0.04;
        camera.lookAt(0, 0, 0);

        const m = morphAmount(t);
        const transitioning = m > 0.0001 && m < 0.9999;
        let cl = null;
        if (hovering) {
          group.updateMatrixWorld();
          const halfH = Math.tan((46 / 2) * Math.PI / 180) * camera.position.z;
          const halfW = halfH * camera.aspect;
          cursor.set(mx * 2 * halfW + camera.position.x, -my * 2 * halfH, 0);
          group.worldToLocal(cursor); cl = cursor;
        }

        for (let i = 0; i < N; i++) {
          const i3 = i * 3;
          let pp = (m - delay[i] * SPREAD) / (1 - SPREAD);
          pp = pp < 0 ? 0 : pp > 1 ? 1 : pp; pp = smooth(pp);
          const scat = Math.sin(pp * Math.PI) * 0.55;
          let tx = A.pos[i3] + (B.pos[i3] - A.pos[i3]) * pp + sdx[i] * scat;
          let ty = A.pos[i3 + 1] + (B.pos[i3 + 1] - A.pos[i3 + 1]) * pp + sdy[i] * scat;
          const tz = A.pos[i3 + 2] + (B.pos[i3 + 2] - A.pos[i3 + 2]) * pp + sdz[i] * scat;
          tx += Math.sin(ph[i] + t * 0.9) * 0.028;
          ty += Math.cos(ph[i] + t * 0.8) * 0.028;
          disp[i3] *= EASE; disp[i3 + 1] *= EASE;
          if (cl) {
            const ddx = tx - cl.x, ddy = ty - cl.y; const dd = Math.sqrt(ddx * ddx + ddy * ddy);
            if (dd < RAD && dd > 0.0001) { const f = (1 - dd / RAD) * STR; disp[i3] += (ddx / dd) * f; disp[i3 + 1] += (ddy / dd) * f; }
          }
          pos[i3] = tx + disp[i3]; pos[i3 + 1] = ty + disp[i3 + 1]; pos[i3 + 2] = tz;

          if (transitioning) {
            cVH.setRGB(A.col[i3], A.col[i3 + 1], A.col[i3 + 2]);
            cB.setRGB(B.col[i3], B.col[i3 + 1], B.col[i3 + 2]);
            cM.copy(cVH).lerp(cB, pp);
            col[i3] = cM.r; col[i3 + 1] = cM.g; col[i3 + 2] = cM.b;
          }
        }
        geo.attributes.position.needsUpdate = true;
        if (transitioning) geo.attributes.color.needsUpdate = true;
        renderer.render(scene, camera);
        raf = requestAnimationFrame(render);
      };
      render();
    }).catch(() => {});

    return () => {
      disposed = true;
      cancelAnimationFrame(raf); ro.disconnect();
      if (!reduce) { window.removeEventListener('pointermove', onPointer); mount.removeEventListener('pointerleave', onLeave); }
      renderer.dispose();
      if (geo) geo.dispose(); if (mat) mat.dispose(); if (dotTex) dotTex.dispose();
      if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
    };
  }, [light, bgColor, transparent]);

  return <div ref={mountRef} className="absolute inset-0 w-full h-full" aria-hidden="true" />;
}
